/**
 * Google Analytics 4 Integration
 *
 * Production-only analytics with cookie consent compliance
 */

const CONSENT_KEY = 'ncesp_analytics_consent'
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development'

// Track if analytics is ready
let analyticsReady = false

// Declare window.dataLayer with proper typing
declare global {
  interface Window {
    dataLayer?: unknown[]

    gtag?: (..._args: any[]) => void
  }
}

/**
 * Check if analytics should be enabled
 */
export const isAnalyticsEnabled = (): boolean => {
  return ENVIRONMENT === 'production' && !!GA_MEASUREMENT_ID
}

/**
 * Check if user has given consent
 */
export const hasAnalyticsConsent = (): boolean => {
  return localStorage.getItem(CONSENT_KEY) === 'true'
}

/**
 * Set analytics consent
 */
export const setAnalyticsConsent = (consent: boolean): void => {
  localStorage.setItem(CONSENT_KEY, consent.toString())
}

/**
 * Revoke analytics consent
 */
export const revokeAnalyticsConsent = (): void => {
  localStorage.removeItem(CONSENT_KEY)
  // Disable further tracking
  if (typeof window !== 'undefined' && window.gtag !== undefined) {
    ;(window.gtag as any)('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
    })
  }
}

/**
 * Initialize Google Analytics
 * Only loads script if consent given and environment is production
 */
export const initializeAnalytics = (): Promise<void> => {
  return new Promise(resolve => {
    if (!isAnalyticsEnabled() || !hasAnalyticsConsent()) {
      resolve()
      return
    }

    // Check if already loaded
    if (analyticsReady && document.querySelector(`script[src*="gtag/js"]`)) {
      resolve()
      return
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || []
    
    // Define gtag function
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments)
    }

    // Load gtag.js script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`

    script.onload = () => {
      // Configure GA4
      window.gtag!('js', new Date())
      window.gtag!('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
        send_page_view: false, // We'll handle page views manually
      })

      analyticsReady = true
      console.log('[Analytics] GA4 initialized:', GA_MEASUREMENT_ID)
      resolve()
    }

    script.onerror = () => {
      console.error('[Analytics] Failed to load GA4 script')
      resolve()
    }

    document.head.appendChild(script)
  })
}

/**
 * Track page view
 */
export const trackPageView = (path: string, title: string): void => {
  if (
    !isAnalyticsEnabled() ||
    !hasAnalyticsConsent() ||
    !analyticsReady ||
    !window.gtag
  ) {
    return
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  })

  console.log('[Analytics] Page view:', { path, title })
}

/**
 * Track custom event
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
): void => {
  if (
    !isAnalyticsEnabled() ||
    !hasAnalyticsConsent() ||
    !analyticsReady ||
    !window.gtag
  ) {
    return
  }

  window.gtag('event', eventName, params)

  console.log('[Analytics] Event:', eventName, params)
}

/**
 * Track outbound link click
 */
export const trackOutboundLink = (url: string, label?: string): void => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: label || url,
    value: url,
  })
}

/**
 * Track social media click
 */
export const trackSocialClick = (platform: string, url: string): void => {
  trackEvent('social_click', {
    social_network: platform,
    social_action: 'click',
    social_target: url,
  })
}
